<template>
  <span></span>
</template>

<script lang="ts" setup>
import { useRouter } from "vue-router"
import { validate as uuidValidate } from "uuid"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"

import getPaymentAction from "@/composables/getPaymentAction"
import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"
import ErrorService from "@/services/errorService"
import { UserFlow } from "@/services/gtmService"

import { ApiError } from "@/shared/apiClient"
import StripePrice, { StripePriceValues } from "@/shared/enums/StripePrice"

const reportStore = useReportStore()
const userStore = useUserStore()

const router = useRouter()

const initAuthProcess = async (): Promise<void> => {
  // Get the authKey from the URL and remove it from the URL
  const url = new URL(window.location.href)
  const authKey = url.searchParams.get("authKey")
  const redirect = url.searchParams.get("redirect") || localStorage.getItem("redirect")
  const promoCode = url.searchParams.get("promo_code")
  const checkoutSessionPrice = url.searchParams.get("checkout_price")
  let query = {}

  // Log the user in with the authKey
  if (authKey) {
    url.searchParams.delete("authKey")
    window.history.replaceState({}, document.title, url)

    try {
      // Attempt to log the user in
      await api.auth.postMagicLogin(authKey)

      await userStore.loadUserData()
      if (userStore.isReportUser) await reportStore.loadReport()
    } catch (error) {
      if (redirect) localStorage.setItem("redirect", redirect)
      ErrorService.redirect(error as ApiError)
    }
  }

  // Save any promo codes in session storage and prepare query object
  if (promoCode) {
    sessionStorage.setItem("promo_code", promoCode)
    query = { promo_code: promoCode }
  }

  if (
    authKey &&
    uuidValidate(checkoutSessionPrice) &&
    StripePriceValues.includes(checkoutSessionPrice as StripePrice)
  ) {
    const paymentAction = getPaymentAction(
      [checkoutSessionPrice as StripePrice],
      UserFlow.COACHING,
      /**beforeChangeUrl*/ undefined,
      sessionStorage.getItem("promo_code") || undefined
    )

    try {
      return await paymentAction()
    } catch (error) {
      console.error(
        `Failed to create and navigate to checkout session with provided stripePrice ${checkoutSessionPrice}`,
        error
      )
    }
  }

  // Redirect the user to the appropriate page
  // We try this, even if the user is not logged in
  // Redirect will be set again if they are not allowed to see the page
  localStorage.removeItem("redirect")
  if (redirect) router.push({ path: redirect, query })
  else if (userStore.isCoachingHasEngaged)
    router.push({ name: GlobalVariables.urls.coaching.children.home.routeName, query })
  else router.push({ path: GlobalVariables.urls.report.path, query })

  document.getElementById("loading")?.remove()
}

initAuthProcess()
</script>
